<script lang="ts">
    import * as Chart from "../../lib/components/ui/chart/index.js";
    import { getClient } from "../../lib/utils.js";
    import { locale } from "../../lib/locale.svelte.js";
    import { useURL } from "../../lib/url.svelte.js";
    import { BarChart, LineChart, AreaChart, Text, accessor } from "layerchart";
    import {
        BigNumber,
        formatDate,
        parseISO,
        todayLocal,
        CalendarDate,
        singleOrDie,
        z,
        CalendarDateSchema,
        startOfWeek,
        singleToArrayCodec,
        endOfWeek,
    } from "@repo/utils";
    import * as Card from "../../lib/components/ui/card/index.js";
    import Number from "../../lib/components/common/number.svelte";
    import * as Select from "../../lib/components/ui/select/index.js";
    import { m } from "../../lib/messages.svelte.js";
    import * as Popover from "../../lib/components/ui/popover";
    import { Button } from "../../lib/components/ui/button/index.js";
    import CalendarIcon from "@lucide/svelte/icons/calendar";
    import { RangeCalendar } from "../../lib/components/ui/range-calendar/index.js";
    import DateComponent from "../../lib/components/common/date.svelte";
    import type { DateRange } from "bits-ui";
    import * as Tabs from "../../lib/components/ui/tabs/index.js";
    import DashboardTransactions from "./transactions-table-v2.svelte";
    // import OcDashboardTransactions from "./oc-transactions-table-v2.svelte";
    import HintLabel from "./hint-label.svelte";
    import { InboxIdSchema } from "@repo/backend/shared";
    import type { InboxId } from "@repo/backend/types";
    import { useOrganization } from "../../lib/auth.svelte.js";
    import type { Res } from "@repo/backend/client";
    import Combobox from "../../lib/components/common/combobox.svelte";
    import { curveBumpX } from "d3-shape";
    import { scalePoint } from "d3-scale";
    import Truncated from "../../lib/components/common/truncated.svelte";

    const organization = useOrganization();
    const useCase = $derived(organization.metadata["useCase"] === "OC" ? ("OC" as const) : ("SO" as const));

    const url = useURL({
        by: z.enum(["day", "week", "month"]),
        tab: z.enum(["overview", "transactions"]),
        from: CalendarDateSchema,
        to: CalendarDateSchema,
        inboxId: singleToArrayCodec(InboxIdSchema),
        partner: z.string(),
    });
    const by = $derived(url.query.by ?? "week");
    const from = $derived(url.query.from ?? startOfWeek(todayLocal().subtract({ weeks: 5 }), locale.current));
    const to = $derived(url.query.to ?? endOfWeek(todayLocal(), locale.current));
    const tab = $derived(url.query.tab ?? "overview");
    const inboxIds = $derived((url.query.inboxId ?? []) as InboxId[]);
    const partner = $derived(url.query.partner && url.query.partner.length > 0 ? url.query.partner : undefined);
    const period = $derived({ start: from as CalendarDate, end: to as CalendarDate });
    let tempRange = $state(period as DateRange);
    let calendarOpen = $state(false);

    $effect(() => {
        if (tempRange.start && tempRange.end) {
            url.query.from = tempRange.start as CalendarDate;
            url.query.to = tempRange.end as CalendarDate;
            calendarOpen = false;
        }
    });

    let meta = $state<Res["Get dashboard meta"]>({ inboxes: [], partnerNames: [] });
    $effect(() => {
        getClient()
            ["Get dashboard meta"]({})
            .then((x) => (meta = x));
    });

    async function fetchData(
        _by: typeof by,
        _period: typeof period,
        _inboxIds: InboxId[],
        _partner: string | undefined,
    ) {
        const response = await getClient()["Get dashboard data v2"]({
            query: {
                by: _by === "day" ? "date_id" : _by === "week" ? "week_start" : "month_start",
                from: _period.start.toString(),
                to: _period.end.toString(),
                inboxIds: _inboxIds.length > 0 ? _inboxIds.join(",") : undefined,
                partnerName: _partner,
            },
        });
        return { by: _by, response };
    }
    let promise = $derived(fetchData(by, period, inboxIds, partner));
    $effect(() => {
        const id = setInterval(() => {
            promise = fetchData(by, period, inboxIds, partner);
        }, 240_000);
        return () => {
            clearInterval(id);
        };
    });

    async function fetchTotals(_period: typeof period, _inboxIds: InboxId[], _partner: string | undefined) {
        return getClient()["Get dashboard data v2"]({
            query: {
                from: _period.start.toString(),
                to: _period.end.toString(),
                inboxIds: _inboxIds.length > 0 ? _inboxIds.join(",") : undefined,
                partnerName: _partner,
            },
        });
    }
    let promiseTotals = $derived(fetchTotals(period, inboxIds, partner));
    $effect(() => {
        const id = setInterval(() => {
            promiseTotals = fetchTotals(period, inboxIds, partner);
        }, 240_000);
        return () => {
            clearInterval(id);
        };
    });

    const dateFormatter = $derived(
        new Intl.DateTimeFormat(locale.current, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }),
    );
    const monthFormatter = $derived(new Intl.DateTimeFormat(locale.current, { month: "short" }));
    const formatters: Record<string, (x: string) => string> = {
        day: (x) => dateFormatter.format(parseISO(x)),
        week: (x) => formatDate(x, "RRRR-II"),
        month: (x) => monthFormatter.format(parseISO(x)),
    };

    const texts = {
        caseTotalCount: [m["Cases detected"], m["Total number of cases detected in incoming emails."]],
        caseHandledCount: [m["Cases handled"], m["Total number of cases successfully handled by turian."]],
        caseHandledRatio: [m["Handled with turian"], m["Share of cases successfully handled by turian."]],
        caseHandledUneditedRatio: [
            m["Cases w/o correction"],
            m["Share of cases processed without errors out of all cases successfully processed by turian."],
        ],
        decisionProcessedUneditedRatio: [
            m["Accuracy"],
            m["Share of correctly interpreted data points of all data points analyzed by turian."],
        ],
        caseErrorRatio: [
            m["Processing Errors"],
            m["Share of cases not successfully handled by turian out of all detected cases."],
        ],
        caseAutomatedRatio: [
            m["Full automation"],
            m[
                "Share of cases automatically submitted to the ERP by turian out of all cases successfully processed, including cases where processing tolerances were applied before submission."
            ],
        ],
    } as const;
</script>

<Tabs.Root bind:value={() => tab, (x) => (url.query.tab = x)} class="flex h-full flex-col overflow-hidden *:px-4">
    <div class="flex items-center gap-4 border-b p-2">
        <!-- <Tabs.List class="h-10">
            <Tabs.Trigger value="overview" class="px-6">
                {m["dashboard.tabs.overview"]()}
            </Tabs.Trigger>
            <Tabs.Trigger value="transactions" class="px-6">
                {m["dashboard.tabs.cases"]()}
            </Tabs.Trigger>
        </Tabs.List> -->
        <div class="flex-1"></div>
        <div class="flex items-end gap-4">
            {#if meta.inboxes.length > 1}
                <Select.Root
                    type="multiple"
                    bind:value={
                        () => inboxIds,
                        (x) => {
                            url.query.inboxId = x;
                        }
                    }
                >
                    <Select.Trigger id="inbox" class="w-60">
                        <Truncated
                            value={inboxIds.length === 0
                                ? m["All inboxes"]()
                                : meta.inboxes
                                      .filter((x) => inboxIds.includes(x.inboxId as InboxId))
                                      .map((x) => x.email)
                                      .join(", ")}
                        />
                    </Select.Trigger>
                    <Select.Content>
                        <Button
                            variant="ghost"
                            size="sm"
                            class="w-full justify-start"
                            onclick={() => {
                                delete url.query.inboxId;
                            }}>{m["all"]()}</Button
                        >
                        {#each meta.inboxes as inbox (inbox.inboxId)}
                            <Select.Item value={inbox.inboxId}>{inbox.email}</Select.Item>
                        {/each}
                    </Select.Content>
                </Select.Root>
            {/if}
            {#if meta.partnerNames.length > 1}
                <Combobox
                    bind:value={
                        () => partner ?? m["All partners"](),
                        (x) => {
                            if (x === m["All partners"]()) {
                                delete url.query.partner;
                            } else {
                                url.query.partner = x ?? undefined;
                            }
                        }
                    }
                    items={[m["All partners"](), ...meta.partnerNames].map((x) => ({ id: x, text: x }))}
                    triggerClass="w-60"
                />
            {/if}
            {#if tab === "overview"}
                <Select.Root type="single" bind:value={() => by, (x) => (url.query.by = x)}>
                    <Select.Trigger id="by" class="w-35">{m[`dashboard.${by}`]()}</Select.Trigger>
                    <Select.Content>
                        <Select.Item value="day">{m["dashboard.day"]()}</Select.Item>
                        <Select.Item value="week">{m["dashboard.week"]()}</Select.Item>
                        <Select.Item value="month">{m["dashboard.month"]()}</Select.Item>
                    </Select.Content>
                </Select.Root>
            {/if}
            <Popover.Root
                onOpenChangeComplete={() => {
                    tempRange = period;
                }}
                bind:open={calendarOpen}
            >
                <Popover.Trigger id="range">
                    {#snippet child({ props })}
                        <Button {...props} variant="outline" class="flex w-60">
                            <CalendarIcon />
                            <DateComponent value={period.start} /> - <DateComponent value={period.end} />
                        </Button>
                    {/snippet}
                </Popover.Trigger>
                <Popover.Content class="w-auto overflow-hidden p-0" align="end">
                    <RangeCalendar
                        class="w-full"
                        fixedWeeks
                        weekStartsOn={1}
                        bind:value={tempRange}
                        locale={locale.current}
                    />
                </Popover.Content>
            </Popover.Root>
        </div>
    </div>

    {#if tab === "overview"}
        <div class="flex flex-col gap-4 overflow-auto py-4.25">
            {#await promiseTotals then data}
                {@const totals = singleOrDie(data)}
                <div class="flex gap-4">
                    {#each ["caseTotalCount", "caseHandledCount", "caseHandledRatio", "caseHandledUneditedRatio", "decisionProcessedUneditedRatio", "caseAutomatedRatio", "caseErrorRatio"] as const as x (x)}
                        <Card.Root class="grow basis-0 justify-between text-center">
                            <Card.Header class="flex grow items-center">
                                <Card.Title class="grow">
                                    <HintLabel label={texts[x][0]()} hint={texts[x][1]()} />
                                </Card.Title>
                            </Card.Header>
                            <Card.Content>
                                <Number
                                    value={BigNumber(totals[x] ?? 0)}
                                    options={x.endsWith("Ratio")
                                        ? {
                                              style: "percent",
                                              minimumFractionDigits: 1,
                                              maximumFractionDigits: 1,
                                          }
                                        : undefined}
                                />
                            </Card.Content>
                        </Card.Root>
                    {/each}
                </div>
            {/await}
            {#await promise then data}
                {#key data.by}
                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>
                                {m["Case type"]()}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={{}} class="aspect-auto h-75">
                                <BarChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    seriesLayout="group"
                                    series={[
                                        {
                                            key: "caseTotalCount",
                                            color: "var(--chart-1)",
                                            label: m["OC"](),
                                        },
                                    ]}
                                    props={{
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet aboveMarks({ context, visibleSeries })}
                                        {#each visibleSeries as s}
                                            {#each data.response as d}
                                                {@const valueAccessor = accessor(s.key)}
                                                {@const value = valueAccessor(d)}
                                                <Text
                                                    x={context.xScale(
                                                        d[
                                                            data.by === "day"
                                                                ? "dateId"
                                                                : data.by === "week"
                                                                  ? "weekStart"
                                                                  : "monthStart"
                                                        ],
                                                    ) +
                                                        (context.x1Scale?.(s.key) ?? 0) +
                                                        (context.x1Scale?.bandwidth?.() ?? 0) / 2}
                                                    y={context.yScale(value)}
                                                    {value}
                                                    textAnchor="middle"
                                                    class="text-xs"
                                                />
                                            {/each}
                                        {/each}
                                    {/snippet}
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]} />
                                    {/snippet}
                                </BarChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>
                    {@const caseStatusChartConfig = {
                        caseHandledCount: {
                            color: "var(--chart-4)",
                            label: m[`handled`](),
                        },
                        caseReviewCount: {
                            color: "var(--chart-6)",
                            label: m[`workbench.caseStatus.REVIEW`](),
                        },
                        caseApprovalCount: {
                            color: "var(--chart-7)",
                            label: m[`workbench.caseStatus.APPROVAL`](),
                        },
                        caseCancelledCount: {
                            color: "var(--chart-10)",
                            label: m[`workbench.caseStatus.CANCELLED`](),
                        },
                        caseErrorCount: {
                            color: "var(--chart-2)",
                            label: m[`workbench.caseStatus.ERROR`](),
                        },
                    }}
                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>
                                {m["Case status"]()}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={caseStatusChartConfig} class="aspect-auto h-75">
                                <BarChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    yPadding={[0, 20]}
                                    seriesLayout="group"
                                    series={(
                                        Object.keys(caseStatusChartConfig) as (keyof typeof caseStatusChartConfig)[]
                                    ).map((x) => ({
                                        key: x,
                                        color: caseStatusChartConfig[x].color,
                                        label: caseStatusChartConfig[x].label,
                                    }))}
                                    props={{
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet aboveMarks({ context, visibleSeries })}
                                        {#each visibleSeries as s}
                                            {#each data.response as d}
                                                {@const valueAccessor = accessor(s.key)}
                                                {@const value = valueAccessor(d)}
                                                <Text
                                                    x={context.xScale(
                                                        d[
                                                            data.by === "day"
                                                                ? "dateId"
                                                                : data.by === "week"
                                                                  ? "weekStart"
                                                                  : "monthStart"
                                                        ],
                                                    ) +
                                                        (context.x1Scale?.(s.key) ?? 0) +
                                                        (context.x1Scale?.bandwidth?.() ?? 0) / 2}
                                                    y={context.yScale(value)}
                                                    {value}
                                                    textAnchor="middle"
                                                    class="text-xs"
                                                />
                                            {/each}
                                        {/each}
                                    {/snippet}
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]} />
                                    {/snippet}
                                </BarChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>
                    {@const fullAutomationChartConfig = {
                        caseAutomatedFullRatio: {
                            color: "var(--chart-4)",
                            label: m[`No deviations`](),
                        },
                        caseAutomatedFullTolerancesRatio: {
                            color: "var(--chart-6)",
                            label: m[`Tolerances applied`](),
                        },
                    }}
                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>
                                {m["Full automation"]()}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={fullAutomationChartConfig} class="aspect-auto h-75">
                                <AreaChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    yPadding={[20, 20]}
                                    xScale={scalePoint()}
                                    yBaseline={null}
                                    seriesLayout="stack"
                                    series={(
                                        Object.keys(
                                            fullAutomationChartConfig,
                                        ) as (keyof typeof fullAutomationChartConfig)[]
                                    ).map((x) => ({
                                        key: x,
                                        color: fullAutomationChartConfig[x].color,
                                        label: fullAutomationChartConfig[x].label,
                                    }))}
                                    props={{
                                        yAxis: {
                                            format: (d: number) => `${d * 100}%`,
                                        },
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]}>
                                            {#snippet valueFormatter({ value })}
                                                <Number
                                                    value={BigNumber(value as number)}
                                                    options={{
                                                        style: "percent",
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 1,
                                                    }}
                                                />
                                            {/snippet}
                                        </Chart.Tooltip>
                                    {/snippet}
                                </AreaChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>
                    {@const decisionAccuracyChartConfig = {
                        decisionProcessedUneditedDeliveryTermsRatio: {
                            color: "var(--chart-1)",
                            label: m[`Delivery Terms`](),
                        },
                        decisionProcessedUneditedPaymentTermsRatio: {
                            color: "var(--chart-2)",
                            label: m[`Payment Terms`](),
                        },
                        decisionProcessedUneditedDeliveryAddressRatio: {
                            color: "var(--chart-3)",
                            label: m[`Delivery Address`](),
                        },
                        decisionProcessedUneditedDeliveryDateRatio: {
                            color: "var(--chart-4)",
                            label: m[`Delivery Date`](),
                        },
                        decisionProcessedUneditedPriceRatio: {
                            color: "var(--chart-5)",
                            label: m[`Price`](),
                        },
                        decisionProcessedUneditedQuantityRatio: {
                            color: "var(--chart-6)",
                            label: m[`Quantity`](),
                        },
                        decisionProcessedUneditedReferenceRatio: {
                            color: "var(--chart-7)",
                            label: m[`Reference`](),
                        },
                        decisionProcessedUneditedTotalQuantityRatio: {
                            color: "var(--chart-8)",
                            label: m[`Item Quantity`](),
                        },
                    }}
                    {#await promiseTotals then data}
                        {@const totals = singleOrDie(data)}
                        {@const dpData = Object.entries(decisionAccuracyChartConfig)
                            .map(([key, config]) => ({
                                name: config.label,
                                value: totals[key] ?? 0,
                                color: config.color,
                            }))
                            .sort((a, b) => (b.value as number) - (a.value as number))}
                        {@const dpMin = Math.min(...dpData.map((d) => d.value as number))}
                        <Card.Root>
                            <Card.Header class="text-center">
                                <Card.Title>
                                    {m["Data point accuracy"]()}
                                </Card.Title>
                            </Card.Header>
                            <Card.Content>
                                <Chart.Container config={decisionAccuracyChartConfig} class="aspect-auto h-75">
                                    <BarChart
                                        data={dpData}
                                        y="name"
                                        x="value"
                                        axis="y"
                                        orientation="horizontal"
                                        rule={false}
                                        xDomain={[Math.max(0, Math.floor(dpMin * 20) / 20 - 0.05), null]}
                                        xPadding={[0, 40]}
                                        labels={{
                                            placement: "outside",
                                            offset: 4,
                                            format: (v) => `${(v * 100).toFixed(1)}%`,
                                        }}
                                        padding={{ left: 120 }}
                                        props={{ xAxis: { format: "percent" } }}
                                        c="color"
                                        cRange={dpData.map((d) => d.color)}
                                    >
                                        {#snippet tooltip()}
                                            <Chart.Tooltip>
                                                {#snippet valueFormatter({ value })}
                                                    <Number
                                                        value={BigNumber(value as number)}
                                                        options={{
                                                            style: "percent",
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        }}
                                                    />
                                                {/snippet}
                                            </Chart.Tooltip>
                                        {/snippet}
                                    </BarChart>
                                </Chart.Container>
                            </Card.Content>
                        </Card.Root>
                    {/await}
                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>
                                {m["Data point accuracy"]()}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={decisionAccuracyChartConfig} class="aspect-auto h-75">
                                <LineChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    xScale={scalePoint()}
                                    yPadding={[20, 20]}
                                    yBaseline={null}
                                    series={(
                                        Object.keys(
                                            decisionAccuracyChartConfig,
                                        ) as (keyof typeof decisionAccuracyChartConfig)[]
                                    ).map((x) => ({
                                        key: x,
                                        color: decisionAccuracyChartConfig[x].color,
                                        label: decisionAccuracyChartConfig[x].label,
                                    }))}
                                    props={{
                                        spline: { curve: curveBumpX },
                                        yAxis: {
                                            format: (d: number) => `${d * 100}%`,
                                        },
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]}>
                                            {#snippet valueFormatter({ value })}
                                                <Number
                                                    value={BigNumber(value as number)}
                                                    options={{
                                                        style: "percent",
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 1,
                                                    }}
                                                />
                                            {/snippet}
                                        </Chart.Tooltip>
                                    {/snippet}
                                </LineChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>
                {/key}
            {/await}
        </div>
    {:else if tab === "transactions"}
        {#if useCase === "OC"}
            <!-- <OcDashboardTransactions
                from={period.start.toString()}
                to={period.end.toString()}
                {inboxIds}
                partnerName={partner}
            /> -->
        {:else}
            <DashboardTransactions
                from={period.start.toString()}
                to={period.end.toString()}
                {inboxIds}
                partnerName={partner}
            />
        {/if}
    {/if}
</Tabs.Root>
